import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Mail, Send, BellRing } from 'lucide-react';
import { submitToBlackOps, captureAttribution } from '../utils/leadCapture';
import TurnstileWidget from './TurnstileWidget';
import { useTurnstile } from '../hooks/useTurnstile';
import { TURNSTILE_REQUIRED_MESSAGE } from '../utils/turnstile';
import { formatPhoneForCRM } from '../utils/phoneFormatter';
import { useLocation } from 'react-router-dom';
import CountryCodeSelector from './CountryCodeSelector';

interface NewsletterPopupProps {
  formId?: string;
}

const NewsletterPopup: React.FC<NewsletterPopupProps> = ({ formId }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [firstname, setFirstname] = useState('');
  const [lastname, setLastname] = useState('');
  const [company, setCompany] = useState('');
  const [phone, setPhone] = useState('');
  const [countryCode, setCountryCode] = useState('+1');
  const [productInterests, setProductInterests] = useState({
    sulfurBurners: false,
    hydrocoolers: false,
    dpaDrenchers: false,
    filtration: false
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState('');
  const [showFloatingButton, setShowFloatingButton] = useState(false);
  const turnstile = useTurnstile();
  const location = useLocation();
  const popupRef = useRef<HTMLDivElement>(null);

  // Show popup after a delay for new visitors
  useEffect(() => {
    const hasSeenPopup = localStorage.getItem('ctc_newsletter_popup_seen');
    const hasSubscribed = localStorage.getItem('ctc_newsletter_subscribed');
    
    if (!hasSeenPopup && !hasSubscribed) {
      const timer = setTimeout(() => {
        setIsOpen(true);
      }, 3000); // 3 seconds delay
      
      return () => clearTimeout(timer);
    } else {
      // If they've seen it before but didn't subscribe, show the floating button
      if (!hasSubscribed) {
        setShowFloatingButton(true);
      }
    }
  }, []); // Only run once on mount, don't reset on route change

  // Handle click outside to close
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (popupRef.current && !popupRef.current.contains(event.target as Node)) {
        handleClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const handleClose = () => {
    setIsOpen(false);
    localStorage.setItem('ctc_newsletter_popup_seen', 'true');
    
    // Show floating button if they haven't subscribed
    if (!localStorage.getItem('ctc_newsletter_subscribed')) {
      setTimeout(() => {
        setShowFloatingButton(true);
      }, 1000);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email) {
      setError('Please enter your email address');
      return;
    }

    if (!firstname.trim()) {
      setError('Please enter your first name');
      return;
    }

    if (!lastname.trim()) {
      setError('Please enter your last name');
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError('Please enter a valid email address');
      return;
    }

    // Create product interest string from selected checkboxes
    const selectedProducts = [];
    if (productInterests.sulfurBurners) selectedProducts.push('Sulfur Burners');
    if (productInterests.hydrocoolers) selectedProducts.push('Hydrocoolers');
    if (productInterests.dpaDrenchers) selectedProducts.push('DPA Drenchers');
    if (productInterests.filtration) selectedProducts.push('Filtration');

    // Require at least one interest to be selected
    if (selectedProducts.length === 0) {
      setError('Please select at least one product interest');
      return;
    }

    const productInterestString = selectedProducts.join(', ');

    if (!turnstile.token) {
      setError(TURNSTILE_REQUIRED_MESSAGE);
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      const payload = {
        form_type: 'newsletter' as const,
        form_id: 'newsletter_popup',
        first_name: firstname,
        last_name: lastname,
        email,
        company_name: company.trim() || undefined,
        phone: phone.trim() ? formatPhoneForCRM(countryCode, phone) : undefined,
        product_interest: productInterestString,
        source: 'Newsletter Popup',
        turnstile_token: turnstile.token,
        ...captureAttribution(),
      };

      const result = await submitToBlackOps(payload);

      if (result.success) {
        setIsSuccess(true);
        localStorage.setItem('ctc_newsletter_subscribed', 'true');
        localStorage.setItem('ctc_newsletter_popup_seen', 'true');
        setShowFloatingButton(false);

        // Close after success message
        setTimeout(() => {
          setIsOpen(false);
        }, 3000);
      } else {
        console.error('Newsletter capture error:', result.error);
        setError('Something went wrong. Please try again later.');
      }
    } catch (err) {
      console.error('Newsletter submission error:', err);
      setError('Something went wrong. Please try again later.');
    }

    // Turnstile tokens are single-use — clear it so a second submission works.
    turnstile.reset();
    setIsSubmitting(false);
  };

  return (
    <>
      {/* Main Popup */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-50 flex items-start justify-center p-4 overflow-y-auto"
          >
            <motion.div
              ref={popupRef}
              initial={{ scale: 0.9, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.9, y: 20 }}
              className="bg-white rounded-xl shadow-2xl max-w-md w-full overflow-hidden my-8"
            >
              {/* Header with close button */}
              <div className="relative">
                <button
                  onClick={handleClose}
                  className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 z-10 p-2 hover:bg-gray-100 rounded-full transition-colors"
                  aria-label="Close newsletter popup"
                >
                  <X size={28} />
                </button>
                
                {/* Header image/gradient */}
                <div className="bg-gradient-to-r from-ctc-blue to-ctc-blue-dark h-24 flex items-center justify-center">
                  <Mail className="text-white" size={48} />
                </div>
              </div>
              
              <div className="p-6">
                {isSuccess ? (
                  <div className="text-center py-4">
                    <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Send className="text-green-600" size={28} />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Thank You!</h3>
                    <p className="text-gray-600 text-center">
                      You've been successfully subscribed! You'll receive product updates, industry insights, and practical tips.
                    </p>
                  </div>
                ) : (
                  <>
                    <h3 className="text-2xl font-bold text-gray-900 mb-2 text-center">
                      Stay Updated With New Products + Industry Insights
                    </h3>

                    <div className="bg-gradient-to-r from-blue-50 to-sky-50 border border-blue-200 rounded-lg p-4 mb-4">
                      <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                          <Mail className="text-blue-600" size={20} />
                        </div>
                        <div>
                          <h4 className="font-semibold text-blue-800">Get Insights & Tips From 40+ Years in the Field</h4>
                          <p className="text-blue-700 text-sm">Product launches, pH management strategies, seasonal maintenance advice, and grower case studies delivered to your inbox.</p>
                        </div>
                      </div>
                    </div>
                    
                    {error && (
                      <div className="bg-red-50 text-red-700 p-3 rounded-lg mb-4 text-sm">
                        {error}
                      </div>
                    )}
                    
                    <form onSubmit={handleSubmit} className="space-y-4">

                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label htmlFor="firstname" className="block text-sm font-medium text-gray-700 mb-1">
                              First Name*
                            </label>
                            <input
                              type="text"
                              id="firstname"
                              required
                              value={firstname}
                              onChange={(e) => setFirstname(e.target.value)}
                              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-ctc-blue focus:border-transparent transition-colors"
                              placeholder="First name"
                              />
                          </div>
                          <div>
                            <label htmlFor="lastname" className="block text-sm font-medium text-gray-700 mb-1">
                              Last Name*
                            </label>
                            <input
                              type="text"
                              id="lastname"
                              required
                              value={lastname}
                              onChange={(e) => setLastname(e.target.value)}
                              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-ctc-blue focus:border-transparent transition-colors"
                              placeholder="Last name"
                              />
                          </div>
                        </div>
                      
                      <div>
                        <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                          Email Address*
                        </label>
                        <input
                          type="email"
                          id="email"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-ctc-blue focus:border-transparent transition-colors"
                          placeholder="your@email.com"
                        />
                      </div>

                      <div>
                        <label htmlFor="company" className="block text-sm font-medium text-gray-700 mb-1">
                          Company Name
                        </label>
                        <input
                          type="text"
                          id="company"
                          value={company}
                          onChange={(e) => setCompany(e.target.value)}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-ctc-blue focus:border-transparent transition-colors"
                          placeholder="Your company name"
                        />
                      </div>

                      <div>
                        <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
                          Phone Number
                        </label>
                        <div className="flex">
                          <CountryCodeSelector
                            value={countryCode}
                            onChange={setCountryCode}
                          />
                          <input
                            type="tel"
                            id="phone"
                            value={phone}
                            onChange={(e) => setPhone(e.target.value)}
                            className="flex-1 px-4 py-2 border border-l-0 border-gray-300 rounded-r-lg focus:ring-2 focus:ring-ctc-blue focus:border-transparent transition-colors"
                            placeholder="555 123 4567"
                          />
                        </div>
                        <p className="mt-1 text-xs text-gray-500">
                          Select your country code and enter your phone number
                        </p>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-3">
                          I'm interested in: *
                        </label>
                        <div className="grid grid-cols-2 gap-2">
                          <label className="flex items-center">
                            <input
                              type="checkbox"
                              checked={productInterests.sulfurBurners}
                              onChange={(e) => setProductInterests(prev => ({
                                ...prev,
                                sulfurBurners: e.target.checked
                              }))}
                              className="h-4 w-4 text-ctc-blue focus:ring-ctc-blue border-gray-300 rounded"
                            />
                            <span className="ml-2 text-xs text-gray-700">Sulfur Burners</span>
                          </label>
                          <label className="flex items-center">
                            <input
                              type="checkbox"
                              checked={productInterests.hydrocoolers}
                              onChange={(e) => setProductInterests(prev => ({
                                ...prev,
                                hydrocoolers: e.target.checked
                              }))}
                              className="h-4 w-4 text-ctc-blue focus:ring-ctc-blue border-gray-300 rounded"
                            />
                            <span className="ml-2 text-xs text-gray-700">Hydrocoolers</span>
                          </label>
                          <label className="flex items-center">
                            <input
                              type="checkbox"
                              checked={productInterests.dpaDrenchers}
                              onChange={(e) => setProductInterests(prev => ({
                                ...prev,
                                dpaDrenchers: e.target.checked
                              }))}
                              className="h-4 w-4 text-ctc-blue focus:ring-ctc-blue border-gray-300 rounded"
                            />
                            <span className="ml-2 text-xs text-gray-700">DPA Drenchers</span>
                          </label>
                          <label className="flex items-center">
                            <input
                              type="checkbox"
                              checked={productInterests.filtration}
                              onChange={(e) => setProductInterests(prev => ({
                                ...prev,
                                filtration: e.target.checked
                              }))}
                              className="h-4 w-4 text-ctc-blue focus:ring-ctc-blue border-gray-300 rounded"
                            />
                            <span className="ml-2 text-xs text-gray-700">Filtration</span>
                          </label>
                        </div>
                      </div>
                      
                      <TurnstileWidget
                        ref={turnstile.ref}
                        onToken={turnstile.setToken}
                        action="newsletter_popup"
                      />

                      <motion.button
                        type="submit"
                        disabled={isSubmitting}
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        className="w-full bg-gradient-to-r from-ctc-blue to-ctc-blue-dark text-white px-4 py-3 rounded-lg font-semibold hover:from-ctc-blue-dark hover:to-ctc-blue transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                      >
                        {isSubmitting ? (
                          <>
                            <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2" />
                            <span>Subscribing...</span>
                          </>
                        ) : (
                          <span>Subscribe Now</span>
                        )}
                      </motion.button>
                    </form>
                    
                    <p className="text-xs text-gray-500 mt-4 text-center">
                      By subscribing, you agree to receive marketing emails from CTC Waterworks. 
                      You can unsubscribe at any time.
                    </p>
                  </>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      
      {/* Floating Button */}
      <AnimatePresence>
        {showFloatingButton && (
          <motion.button
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            whileHover={{ scale: 1.1 }}
            onClick={() => setIsOpen(true)}
            className="fixed bottom-6 right-6 bg-ctc-blue text-white p-3 rounded-full shadow-lg z-40 flex items-center justify-center"
            aria-label="Subscribe to newsletter"
          >
            <div className="relative">
              <BellRing size={24} />
              <span className="absolute -top-1 -right-1 w-3 h-3 bg-red-500 rounded-full"></span>
            </div>
          </motion.button>
        )}
      </AnimatePresence>
    </>
  );
};

export default NewsletterPopup;